/**
 * Dashboard Theme Configuration
 *
 * Single source of truth for dashboard visual settings.
 * Centralizes colors, chart config, and breakpoints for easy customization.
 *
 * Chart color scheme: Hourglass spruce + sea-glass neutrals.
 * Spruce = primary metric (After / Exported / Hourglass).
 * Sea-glass = secondary metric (Before / Unexported / Other).
 */

// ============================================
// CHART COLORS (Utilization & Export Activity)
// ============================================
// Unified palette: spruce primary + sea-glass secondary

export const CHART_COLORS = {
  /**
   * Primary metric (Hourglass / Exported hours):
   * Spruce — the strongest accent in the brand palette
   */
  exported: {
    fill: "var(--chart-1)",
    stroke: "var(--brand-700)",
  },
  /**
   * Secondary metric (Other / Unexported hours):
   * Sea-glass — neutral baseline that recedes behind the hero color
   */
  unexported: {
    fill: "var(--chart-2)",
    stroke: "var(--neutral-600)",
  },
  /** Grid and axis styling */
  grid: {
    line: "var(--border)",
    axis: "var(--muted-foreground)",
  },
};

// ============================================
// CHART CONFIGURATION
// ============================================

export const CHART_CONFIG = {
  /** Maximum bar width in pixels (prevents comically wide bars with few data points) */
  maxBarSize: 80,
  /** Border radius for top of stacked bars [topLeft, topRight, bottomLeft, bottomRight] */
  topBarRadius: [4, 4, 0, 0] as [number, number, number, number],
  /** No radius for bottom bar in stack */
  bottomBarRadius: [0, 0, 0, 0] as [number, number, number, number],
};

// ============================================
// TEXT COLORS (for tooltips, tables, cards)
// ============================================

export const TEXT_COLORS = {
  exported: {
    light: "text-primary",
    value: "text-primary",
  },
  unexported: {
    light: "text-muted-foreground",
    value: "text-muted-foreground",
  },
};

// ============================================
// SUMMARY CARD STYLES
// ============================================

export const SUMMARY_CARD_STYLES = {
	/** Base card style - compact, clean */
	base: 'p-3 rounded-lg bg-card border border-border shadow-xs',
	/** Accent bar colors (the left vertical stripe) */
	accent: {
		exported: 'bg-chart-1',
		unexported: 'bg-chart-2',
		neutral: 'bg-slate-400 dark:bg-slate-500',
		count: 'bg-slate-400 dark:bg-slate-500',
	},
}

// ============================================
// KPI CARD STYLES (shared across dashboard tabs)
// ============================================

export const KPI_CARD_STYLES = {
  /** Dark green gradient — primary metrics (old dashboard: Exported badge) */
  green: "from-[var(--brand-700)] to-[var(--brand-900)]",
  /** Teal gradient — secondary metrics (old dashboard: Unexported badge) */
  teal: "from-[var(--brand-600)] to-[var(--brand-700)]",
  /** Blue gradient — tertiary metrics (old dashboard: Deleted badge) */
  blue: "from-[var(--chart-3)] to-[var(--brand-700)]",
  /** Dark gradient — neutral/count metrics (old dashboard: Active Users badge) */
  dark: "from-[var(--brand-900)] to-[var(--brand-950)]",
  /** Slate gradient (kept for backward compat if needed) */
  slate: "from-[var(--brand-dark-slate)] to-[var(--brand-mid-slate)]",
} as const;

// ============================================
// YEAR-OVER-YEAR CHART COLORS
// ============================================

export const YOY_CHART_COLORS = {
  before: "var(--chart-2)", // Sea-glass — prior year baseline (recedes)
  after: "var(--chart-1)",  // Spruce — Hourglass period (hero)
} as const;

/** Layout config for the YoY faceted chart grid (all-professionals view) */
export const YOY_CHART_LAYOUT = {
  /** Height (px) for the single full-width chart when one professional is selected */
  singleChartHeight: 350,
  /** Height (px) for each mini-chart in the faceted grid */
  gridChartHeight: 300,
  /** Tailwind grid classes — controls columns and spacing for the faceted layout */
  gridClass: "grid grid-cols-1 lg:grid-cols-2 gap-6",
} as const;

// ============================================
// DAILY VIEW CHART COLORS
// ============================================

export const DAILY_CHART_COLORS = {
  after: "var(--chart-1)",
  before: "var(--chart-2)",
  afterMA: "var(--chart-4)",
  beforeMA: "var(--chart-3)",
} as const;

// ============================================
// UTILIZATION STYLES
// ============================================

export const UTILIZATION_COLORS = {
  hourglass: "var(--chart-1)",
  other: "var(--chart-2)",
} as const;

// ============================================
// PERFORMANCE TABLE STYLES
// ============================================

export const PERF_TABLE_COLORS = {
  utilizationHeader: "var(--brand-700)",
  weekHeader: "var(--brand-600)",
  monthHeader: "var(--status-exported)",
  yearHeader: "var(--status-reviewed)",
  positive: "var(--status-exported)",
  negative: "var(--status-deleted)",
} as const;

// ============================================
// LAYOUT BREAKPOINTS
// ============================================

export const LAYOUT = {
  /** Breakpoint for side-by-side chart layout (Tailwind xl = 1280px) */
  sideBySideBreakpoint: "xl",
  /** Chart container height */
  chartHeight: "h-80",
};
